/**
 * dapp-browser/injection.ts
 *
 * JavaScript code injected into dApp WebViews.
 * Exposes Scatter / eosjs / TokenPocket / WharfKit UAL–style globals so EOS dApps
 * can read the pre-authorised account and sign via postMessage to the host.
 *
 * Security model:
 *   - Private key NEVER exposed to the dApp
 *   - All signing happens in the native wallet context
 *   - dApps send unsigned transaction → bridge → wallet signs → returns result
 */

/** Protocol version embedded in every bridge message (host must match). */
export const BRIDGE_VERSION = '1'

/** Allowed `type` values for dApp → wallet postMessage payloads. */
export const BRIDGE_MESSAGE_TYPES = [
  'transaction',
  'getAccount',
  'getChainId',
  'ready',
  'sign',
  'pushTransaction',
  'pushEosAction',
] as const

export type BridgeMessageType = (typeof BRIDGE_MESSAGE_TYPES)[number]

export interface BridgeMessage {
  __pw_bridge: true
  version: string
  id: number
  type: BridgeMessageType
  payload: Record<string, unknown>
}

export interface ProperWalletAccount {
  actor: string
  permission: string
  chainId: string
}

export interface ProperWalletProvider {
  getAccount(): Promise<ProperWalletAccount>
  login(): Promise<ProperWalletAccount>
  logout(): Promise<void>
  signTransaction(args: unknown): Promise<{
    signatures: string[]
  }>
  transact(args: unknown): Promise<unknown>
}

export function validateBridgeMessage(data: unknown): data is BridgeMessage {
  if (typeof data !== 'object' || data === null) return false
  const o = data as Record<string, unknown>
  if (o.__pw_bridge !== true) return false
  if (o.version !== BRIDGE_VERSION) return false
  if (typeof o.id !== 'number' || !Number.isFinite(o.id)) return false
  if (typeof o.type !== 'string') return false
  if (!BRIDGE_MESSAGE_TYPES.includes(o.type as BridgeMessageType)) return false
  if (typeof o.payload !== 'object' || o.payload === null || Array.isArray(o.payload)) return false
  return true
}

/**
 * Generate the JavaScript string to inject into a WebView.
 * Parametrised with the current account so dApps can read it synchronously.
 * `parentOrigin` is used for postMessage targetOrigin (never '*').
 */
export function buildInjectionScript(accountName: string, chainId: string, parentOrigin: string): string {
  const safeAcc = JSON.stringify(accountName)
  const safeChain = JSON.stringify(chainId)
  const safeOrigin = JSON.stringify(parentOrigin)
  const v = JSON.stringify(BRIDGE_VERSION)

  return `
(function() {
  'use strict';

  // Prevent double-injection
  if (window.__properwallet_injected) return;
  window.__properwallet_injected = true;

  var PARENT_ORIGIN = ${safeOrigin};
  var ACCOUNT = ${safeAcc};
  var CHAIN = ${safeChain};
  var VER = ${v};

  // ── Message bridge ──────────────────────────────────────────────────────────

  var _requestId = 0;
  var _pendingRequests = new Map();
  var _properwalletRequestId = 0;
  var _properwalletPending = new Map();

  function postToHost(msgObj) {
    if (window.ProperWalletBridge && window.ProperWalletBridge.postMessage) {
      window.ProperWalletBridge.postMessage(JSON.stringify(msgObj));
    } else if (window.mobileApp && window.mobileApp.postMessage) {
      window.mobileApp.postMessage({ detail: msgObj });
    } else if (window.parent !== window) {
      window.parent.postMessage(msgObj, PARENT_ORIGIN);
    }
  }

  function bridgeSend(type, payload) {
    return new Promise(function(resolve, reject) {
      var id = ++_requestId;
      _pendingRequests.set(id, { resolve: resolve, reject: reject });

      postToHost({
        __pw_bridge: true,
        version: VER,
        id: id,
        type: type,
        payload: payload || {}
      });

      setTimeout(function() {
        if (_pendingRequests.has(id)) {
          _pendingRequests.delete(id);
          reject(new Error('ProperWallet: request timed out'));
        }
      }, 60000);
    });
  }

  function properwalletSend(type, payload) {
    return new Promise(function(resolve, reject) {
      var requestId = 'pw-' + (++_properwalletRequestId) + '-' + Date.now();

      function armTimeout() {
        return setTimeout(function() {
          if (_properwalletPending.has(requestId)) {
            _properwalletPending.delete(requestId);
            reject(new Error('ProperWallet: request timed out'));
          }
        }, 60000);
      }

      // 'timeoutId' is re-armed whenever the native side sends a
      // 'properwallet:pending' keep-alive (e.g. while the user is entering
      // their PIN or session.transact() is still in flight), so the 60s
      // window only measures actual silence, not total round-trip time.
      var entry = { resolve: resolve, reject: reject, timeoutId: armTimeout() };
      _properwalletPending.set(requestId, entry);

      postToHost(Object.assign({}, payload || {}, {
        type: type,
        requestId: requestId
      }));
    });
  }

  function properwalletSessionToAccount(session) {
    return {
      actor: session.actor || session.accountName || ACCOUNT,
      permission: session.permission || 'active',
      chainId: session.chainId || CHAIN
    };
  }

  /**
   * Host reply validation:
   * - iframe: require source === parent AND origin === PARENT_ORIGIN (AND)
   * - native InAppBrowser (top-level): host injects MessageEvent with PARENT_ORIGIN
   */
  function acceptHostMessage(ev) {
    if (window.parent !== window) {
      return ev.source === window.parent && ev.origin === PARENT_ORIGIN;
    }
    return ev.origin === PARENT_ORIGIN;
  }

  window.__pw_resolve = function(id, result) {
    var req = _pendingRequests.get(id);
    if (req) { _pendingRequests.delete(id); req.resolve(result); }
  };
  window.__pw_reject = function(id, error) {
    var req = _pendingRequests.get(id);
    if (req) { _pendingRequests.delete(id); req.reject(new Error(error)); }
  };

  window.addEventListener('message', function(ev) {
    if (!acceptHostMessage(ev)) return;
    var d = ev.data;
    if (typeof d !== 'object' || d === null || !d.__pw_response) return;
    if (typeof d.id !== 'number') return;
    if (d.error) {
      window.__pw_reject(d.id, d.error);
    } else {
      window.__pw_resolve(d.id, d.result);
    }
  });

  window.addEventListener('message', function(ev) {
    if (!acceptHostMessage(ev)) return;
    var d = ev.data;
    if (typeof d !== 'object' || d === null || typeof d.type !== 'string') return;
    if (typeof d.requestId !== 'string') return;
    var req = _properwalletPending.get(d.requestId);
    if (!req) return;

    if (d.type === 'properwallet:pending') {
      // Native side is still working (PIN entry / session.transact in
      // flight). Reset the timeout instead of letting it expire.
      clearTimeout(req.timeoutId);
      req.timeoutId = setTimeout(function() {
        if (_properwalletPending.has(d.requestId)) {
          _properwalletPending.delete(d.requestId);
          req.reject(new Error('ProperWallet: request timed out'));
        }
      }, 60000);
      return;
    }

    if (d.type === 'properwallet:error') {
      clearTimeout(req.timeoutId);
      _properwalletPending.delete(d.requestId);
      req.reject(new Error(d.error || 'ProperWallet request failed'));
      return;
    }

    if (
      d.type === 'properwallet:session' ||
      d.type === 'properwallet:signatures' ||
      d.type === 'properwallet:transact-result'
    ) {
      clearTimeout(req.timeoutId);
      _properwalletPending.delete(d.requestId);
      req.resolve(d);
    }

    if (d.type === 'properwallet:sign-result') {
      clearTimeout(req.timeoutId);
      _properwalletPending.delete(d.requestId);
      req.resolve(d);
    }
  });

  // ── window.properwallet ───────────────────────────────────────────────────

  window.properwallet = {
    isProperWallet: true,
    version: '1.0.0',

    getAccount: function() {
      return properwalletSend('properwallet:request-session', { reason: 'getAccount' })
        .then(properwalletSessionToAccount);
    },

    login: function() {
      return properwalletSend('properwallet:request-session', { reason: 'login' })
        .then(properwalletSessionToAccount);
    },

    logout: function() {
      window.dispatchEvent(new CustomEvent('properwallet:logout'));
      return Promise.resolve();
    },

    signTransaction: function(args) {
      if (!args || typeof args !== 'object') {
        return Promise.reject(new Error('ProperWallet: signTransaction requires args'));
      }
      return properwalletSend('properwallet:sign-transaction', {
        args: args
      }).then(function(response) {
        return response.result;
      });
    },

    transact: function(args) {
      if (!args || typeof args !== 'object') {
        return Promise.reject(new Error('ProperWallet: transact requires args'));
      }
      return properwalletSend('properwallet:transact', {
        args: args
      }).then(function(response) {
        return response.result;
      });
    }
  };

  // ── window.webdex ──────────────────────────────────────────────────────────

  window.webdex = {
    getVersion: function() { return '1.0.0'; },
    getAccount: function() { return ACCOUNT; },
    getChainId: function() { return CHAIN; },

    requestAccount: function() {
      return bridgeSend('getAccount', { op: 'requestAccount' });
    },

    requestChainId: function() {
      return bridgeSend('getChainId', {});
    },

    signTransaction: function(transaction) {
      return bridgeSend('transaction', { op: 'signTransaction', transaction: transaction });
    },

    pushTransaction: function(transaction) {
      return bridgeSend('transaction', { op: 'pushTransaction', transaction: transaction });
    },

    signArbitrary: function(data) {
      return bridgeSend('sign', { op: 'signArbitrary', data: data });
    },
  };

  // ── window.eos (eosjs-style) ───────────────────────────────────────────────

  window.eos = {
    getIdentity: function() {
      return Promise.resolve({
        accounts: [{ name: ACCOUNT, authority: 'active' }]
      });
    },

    transaction: function(tx) {
      return bridgeSend('transaction', { op: 'signTransaction', transaction: tx });
    },

    getArbitrarySignature: function(account, data) {
      return bridgeSend('sign', { op: 'getArbitrarySignature', account: account, data: data });
    },

    pushTransaction: function(args) {
      var p = Object.assign({}, args || {});
      p.op = 'pushTransaction';
      return bridgeSend('transaction', p);
    },
  };

  // ── window.scatter (Scatter / @wharfkit/protocol-scatter) ───────────────────

  window.scatter = {
    isConnected: true,
    isExtension: false,
    identity: {
      accounts: [{ name: ACCOUNT, authority: 'active', blockchain: 'eos' }]
    },

    connect: function(appName) {
      return Promise.resolve(true);
    },

    getIdentity: function(requiredFields) {
      return Promise.resolve(window.scatter.identity);
    },

    authenticate: function() {
      return bridgeSend('sign', { op: 'authenticate' });
    },

    pushTransaction: function(tx) {
      return bridgeSend('pushTransaction', { op: 'pushTransaction', transaction: tx });
    },

    requireVersion: function() {},

    suggestNetwork: function() {
      return Promise.resolve(true);
    },
  };

  // ── WharfKit UAL-style provider ─────────────────────────────────────────────

  window.__wharfkit_provider = {
    name: 'ProperWallet',
    version: '1.0.0',

    getChainId: function() {
      return CHAIN;
    },

    getAccountName: function() {
      return ACCOUNT;
    },

    login: function() {
      return Promise.resolve({
        accountName: ACCOUNT,
        actor: ACCOUNT,
        permission: 'active',
        chainId: CHAIN,
        accounts: [{ name: ACCOUNT, authority: 'active', blockchain: 'eos' }]
      });
    },

    transact: function(args) {
      if (!args || typeof args !== 'object') {
        return Promise.reject(new Error('ProperWallet: invalid transact args'));
      }
      return bridgeSend('transaction', Object.assign({}, args, { op: 'signTransaction' }));
    },
  };

  // ── window.tp (TokenPocket-style) ───────────────────────────────────────────

  window.tp = {
    isConnected: function() {
      return true;
    },

    getCurrentWallet: function() {
      return {
        account: ACCOUNT,
        permissions: 'active',
        blockchain: 'eos'
      };
    },

    getWalletList: function(blockchain) {
      return Promise.resolve({
        wallets: [{
          account: ACCOUNT,
          permissions: 'active',
          blockchain: blockchain || 'eos'
        }]
      });
    },

    pushEosAction: function(actions) {
      return bridgeSend('pushEosAction', { op: 'pushEosAction', actions: actions });
    },

    sign: function(params) {
      return bridgeSend('sign', Object.assign({ op: 'tpSign' }, params || {}));
    },
  };

  window.tokenpocket = window.tp;
  window.TokenPocket = window.tp;

  window.dispatchEvent(new CustomEvent('properwallet:ready', {
    detail: { account: ACCOUNT, chainId: CHAIN }
  }));
  window.dispatchEvent(new CustomEvent('scatterLoaded'));

  bridgeSend('ready', { account: ACCOUNT, chainId: CHAIN }).catch(function() {});

  console.log('[ProperWallet] Providers injected:', {
    hasEos: !!window.eos,
    hasScatter: !!window.scatter,
    hasTp: !!window.tp,
    hasWharfkit: !!window.__wharfkit_provider,
    hasProperWallet: !!window.properwallet,
    account: ACCOUNT
  });
})();
`
}

/**
 * Parse an incoming bridge message from a WebView (string channel, e.g. native).
 */
export function parseBridgeMessage(raw: string): BridgeMessage | null {
  try {
    const msg: unknown = JSON.parse(raw)
    return validateBridgeMessage(msg) ? msg : null
  } catch {
    return null
  }
}
